import { dayText, galText } from "./accounts";
import type { Route, RouteChange, RouteStop } from "./api";
import { emailName } from "./calls";
import { memberName } from "./memberNames";

const DAY_MS = 86_400_000;
const TRUCK = /^[A-Z0-9]{1,8}$/;
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const STATE_TEXT: Record<RouteStop["state"], string> = {
  planned: "Planned",
  done: "Done",
  skipped: "Skipped",
  measured: "Measured",
  partial: "Partly emptied",
  no_access: "No access",
};

/** "Late by 75 min", or "Late by 2 h 5 min" from two hours (rule O-3). */
export function lateText(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `Late by ${hours >= 2 ? `${hours} h ${rest} min` : `${minutes} min`}`;
}

export function shiftDay(iso: string, days: number): string {
  return new Date(Date.parse(`${iso}T12:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

/** "Tue Oct 14", and "(today)" on the day of the organization. */
export function dayTitle(day: string, today: string): string {
  const weekday = WEEKDAYS[new Date(`${day}T12:00:00Z`).getUTCDay()];
  return `${weekday} ${dayText(day)}${day === today ? " (today)" : ""}`;
}

export function dayFromSearch(search: string): string | undefined {
  const day = new URLSearchParams(search).get("day") ?? "";
  return /^\d{4}-\d{2}-\d{2}$/.test(day) && !Number.isNaN(Date.parse(day)) ? day : undefined;
}

/** The truck label in upper case, or an error text. */
export function truckError(text: string): string | null {
  return TRUCK.test(text.trim().toUpperCase()) ? null : "Enter the truck as 1 to 8 letters and digits, for example T2.";
}

export function driverText(route: Pick<Route, "driver_email">): string {
  return route.driver_email ? emailName(route.driver_email) : "Former member";
}

/** The member name of a driver, else the part of the email before "@". */
export const memberText = (person: { email: string; name?: string }) => person.name || memberName(person.email);

/** The members with the driver role, and the current driver; an older API sends no role, so each member shows. */
export const driverChoices = <T extends { id: string; driver?: boolean }>(drivers: readonly T[], current?: string): T[] =>
  drivers.filter((person) => person.driver !== false || person.id === current);

type StopLoad = Pick<RouteStop, "state" | "raw_gal" | "estimated_gal">;

export const collected = (stop: StopLoad) => (stop.state === "done" || stop.state === "partial") && stop.raw_gal != null;

/** The collected gallons of a done or partly emptied stop, else the estimate. */
export const stopLoad = (stop: StopLoad): number | null => (collected(stop) ? (stop.raw_gal ?? null) : stop.estimated_gal);

export function stopLoadText(stop: StopLoad): string {
  if (collected(stop)) return `${galText(stop.raw_gal ?? 0)} collected`;
  return stop.estimated_gal === null ? "–" : galText(stop.estimated_gal);
}

export function totalsText(route: Pick<Route, "totals" | "size_gal">): string {
  const { stops, gallons, hours, stops_per_hour: rate } = route.totals;
  const time = rate ? `${hours} h at ${rate} stops an hour` : `${hours} h`;
  const parts = [`${stops} ${stops === 1 ? "stop" : "stops"}`, `${galText(gallons)} estimated`, time];
  if (route.size_gal !== null) parts.push(`${galText(route.size_gal)} truck`);
  return parts.join(" · ");
}

/** A warning when the estimated gallons do not fit in the truck. */
export function fitText(route: Pick<Route, "totals" | "size_gal">): string | null {
  if (route.totals.fits !== false || route.size_gal === null) return null;
  return `The estimate is ${galText(route.totals.gallons - route.size_gal)} more than the truck holds.`;
}

export type RouteForm = { truck: string; size: string; driver: string };

/** The fields of a route change that differ from the route; an empty size takes the fleet size. */
export function routeChange(
  route: Pick<Route, "truck" | "size_gal" | "driver_id">,
  form: RouteForm,
  fleetTrucks: readonly { code: string; capacity_gal: number }[],
): RouteChange {
  const truck = form.truck.trim().toUpperCase();
  const fleet = fleetTrucks.find((option) => option.code === truck)?.capacity_gal ?? null;
  const size = form.size ? Number(form.size) : fleet;
  const change: RouteChange = {};
  if (truck !== route.truck) change.truck = truck;
  if (size !== route.size_gal && (form.size || truck !== route.truck)) change.size_gal = size;
  if (form.driver && form.driver !== route.driver_id) change.driver_id = form.driver;
  return change;
}

/** Can the stop at this index move one place up (-1) or down (+1)? */
export const canMove = (index: number, count: number, step: -1 | 1) => index + step >= 0 && index + step < count;

/** Rule V-11: the preferred truck of the driver when no route of the day uses it, else null. */
export function freePreferredTruck(
  drivers: readonly { id: string; preferred_truck?: string | null }[],
  driverId: string,
  routes: readonly Pick<Route, "truck" | "status">[],
): string | null {
  const truck = drivers.find((person) => person.id === driverId)?.preferred_truck ?? null;
  return truck && !routes.some((route) => route.truck === truck && route.status !== "cancelled") ? truck : null;
}
